import { FetchOptions, FetchRequest, FetchResponse } from '../fetch.model';
import { handleFetchError } from './api-error.util';
import { setHeaderIfNotExists } from './headers.util';
import { handleResponse } from './response.util';
import { handleStreamingResponse } from './streaming.util';
import { buildUrl } from './url.util';

function applyExtraHeaders(
  headers: HeadersInit,
  getExtraHeaders?: () => Record<string, string>,
): void {
  const extraHeaders = getExtraHeaders?.() ?? {};

  for (const [name, value] of Object.entries(extraHeaders)) {
    setHeaderIfNotExists({ headers, name, value });
  }
}

export function createGetRequest(baseURL: string, getExtraHeaders?: () => Record<string, string>) {
  return async function get<T = unknown>(
    url: string,
    options: FetchOptions = {},
  ): Promise<FetchResponse<T>> {
    const { keepalive, params, ...fetchOptions } = options;
    const fullUrl = buildUrl(url.startsWith('http') ? url : `${baseURL}${url}`, params);

    const headers: HeadersInit =
      fetchOptions.headers instanceof Headers || Array.isArray(fetchOptions.headers)
        ? fetchOptions.headers
        : { ...fetchOptions.headers };

    applyExtraHeaders(headers, getExtraHeaders);

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        credentials: 'include',
        headers,
        keepalive: keepalive ?? false,
        method: 'GET',
      });

      return await handleResponse<T>(response, fullUrl);
    } catch (error) {
      return handleFetchError(error, fullUrl);
    }
  };
}

export function createSSERequest(baseURL: string, getExtraHeaders?: () => Record<string, string>) {
  return async function sse(url: string, options: FetchOptions = {}): Promise<Response> {
    const { keepalive, params, ...fetchOptions } = options;
    const fullUrl = buildUrl(url.startsWith('http') ? url : `${baseURL}${url}`, params);

    const headers: HeadersInit =
      fetchOptions.headers instanceof Headers || Array.isArray(fetchOptions.headers)
        ? fetchOptions.headers
        : { ...fetchOptions.headers };

    setHeaderIfNotExists({ headers, name: 'Accept', value: 'text/event-stream' });
    applyExtraHeaders(headers, getExtraHeaders);

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        credentials: 'include',
        headers,
        keepalive: keepalive ?? false,
        method: 'POST',
      });

      return await handleStreamingResponse(response, fullUrl);
    } catch (error) {
      return handleFetchError(error, fullUrl);
    }
  };
}

export function createPostRequest(baseURL: string, getExtraHeaders?: () => Record<string, string>) {
  return async function post<T = unknown>(
    url: string,
    request?: FetchRequest,
  ): Promise<FetchResponse<T>> {
    const { keepalive, params, ...fetchOptions } = request?.options ?? {};
    const body = request?.body;
    const fullUrl = buildUrl(url.startsWith('http') ? url : `${baseURL}${url}`, params);

    const headers: HeadersInit =
      fetchOptions.headers instanceof Headers || Array.isArray(fetchOptions.headers)
        ? fetchOptions.headers
        : { ...fetchOptions.headers };

    setHeaderIfNotExists({ headers, name: 'Content-Type', value: 'application/json' });
    applyExtraHeaders(headers, getExtraHeaders);

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'include',
        headers,
        keepalive: keepalive ?? false,
        method: 'POST',
      });

      return await handleResponse<T>(response, fullUrl);
    } catch (error) {
      return handleFetchError(error, fullUrl);
    }
  };
}

export function createPatchRequest(
  baseURL: string,
  getExtraHeaders?: () => Record<string, string>,
) {
  return async function patch<T = unknown>(
    url: string,
    request?: FetchRequest,
  ): Promise<FetchResponse<T>> {
    const { keepalive, params, ...fetchOptions } = request?.options ?? {};
    const body = request?.body;
    const fullUrl = buildUrl(url.startsWith('http') ? url : `${baseURL}${url}`, params);

    const headers: HeadersInit =
      fetchOptions.headers instanceof Headers || Array.isArray(fetchOptions.headers)
        ? fetchOptions.headers
        : { ...fetchOptions.headers };

    setHeaderIfNotExists({ headers, name: 'Content-Type', value: 'application/json' });
    applyExtraHeaders(headers, getExtraHeaders);

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'include',
        headers,
        keepalive: keepalive ?? false,
        method: 'PATCH',
      });

      return await handleResponse<T>(response, fullUrl);
    } catch (error) {
      return handleFetchError(error, fullUrl);
    }
  };
}

export function createPutRequest(baseURL: string, getExtraHeaders?: () => Record<string, string>) {
  return async function put<T = unknown>(
    url: string,
    request?: FetchRequest,
  ): Promise<FetchResponse<T>> {
    const { keepalive, params, ...fetchOptions } = request?.options ?? {};
    const body = request?.body;
    const fullUrl = buildUrl(url.startsWith('http') ? url : `${baseURL}${url}`, params);

    const headers: HeadersInit =
      fetchOptions.headers instanceof Headers || Array.isArray(fetchOptions.headers)
        ? fetchOptions.headers
        : { ...fetchOptions.headers };

    setHeaderIfNotExists({ headers, name: 'Content-Type', value: 'application/json' });
    applyExtraHeaders(headers, getExtraHeaders);

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        body: body !== null && body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'include',
        headers,
        keepalive: keepalive ?? false,
        method: 'PUT',
      });

      return await handleResponse<T>(response, fullUrl);
    } catch (error) {
      return handleFetchError(error, fullUrl);
    }
  };
}

export function createDeleteRequest(
  baseURL: string,
  getExtraHeaders?: () => Record<string, string>,
) {
  return async function del<T = unknown>(
    url: string,
    options: FetchOptions = {},
  ): Promise<FetchResponse<T>> {
    const { keepalive, params, ...fetchOptions } = options;
    const fullUrl = buildUrl(url.startsWith('http') ? url : `${baseURL}${url}`, params);

    const headers: HeadersInit =
      fetchOptions.headers instanceof Headers || Array.isArray(fetchOptions.headers)
        ? fetchOptions.headers
        : { ...fetchOptions.headers };

    applyExtraHeaders(headers, getExtraHeaders);

    try {
      const response = await fetch(fullUrl, {
        ...fetchOptions,
        credentials: 'include',
        headers,
        keepalive: keepalive ?? false,
        method: 'DELETE',
      });

      return await handleResponse<T>(response, fullUrl);
    } catch (error) {
      return handleFetchError(error, fullUrl);
    }
  };
}
